import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { createThread } from '../../Services/ForumService';
import { threadFollowService } from '../../Services/threadFollowService';

import { useAuth } from '../../contexts/AuthContext';

const CreateThreadModal = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (title.length < 10) {
      setError('Le titre doit faire au moins 10 caractères');
      return;
    }

    if (content.length < 20) {
      setError('Le contenu doit faire au moins 20 caractères');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (!user) {
        setError('Vous devez être connecté pour publier');
        return;
      }

      // Créer le thread
      const threadId = await createThread(user.uid, title, content);

      // 🆕 Auto-suivre le thread pour l'auteur
      try {
        await threadFollowService.followThread(user.uid, threadId);
      } catch (err) {
        console.error('Erreur auto-suivi:', err);
        // Ne pas bloquer si erreur de suivi
      }

      onSuccess();
    } catch (err) {
      console.error('Erreur création:', err);
      setError('Erreur lors de la création du sujet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Créer un nouveau sujet
            </h2>
            <button
              onClick={onClose}
              className="text-muted hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Titre du sujet *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Comment gérer le stress de l'aidant ?"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={150}
                disabled={loading}
              />
              <p className="text-xs text-muted mt-1">{title.length}/150 caractères</p>
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Votre message *
              </label>

              {/* 🆕 Barre emojis */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  '❤️',
                  '💪',
                  '👍',
                  '🙏',
                  '💊',
                  '🏥',
                  '😊',
                  '😔',
                  '🤔',
                  '✅',
                  '⚠️',
                  '💙',
                  '🌟',
                  '👏',
                  '🫂',
                ].map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      const textarea = document.getElementById('thread-textarea');
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const newText = content.substring(0, start) + emoji + content.substring(end);
                      setContent(newText);
                      setTimeout(() => {
                        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
                        textarea.focus();
                      }, 0);
                    }}
                    className="text-lg hover:scale-125 transition-transform"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <textarea
                id="thread-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Décrivez votre situation, posez votre question..."
                rows={8}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                maxLength={2000}
                disabled={loading}
              />
              <p className="text-xs text-muted mt-1">{content.length}/2000 caractères</p>
            </div>

            {/* Avertissement */}
            <Card className="p-3 bg-amber-50 border-amber-200">
              <p className="text-xs text-amber-800">
                ⚠️ <strong>Rappel :</strong> Ne partagez aucune information médicale personnelle
                sensible. Ce forum ne remplace pas un avis médical.
              </p>
            </Card>

            {/* Erreur */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Annuler
              </Button>

              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Publier'
                )}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default CreateThreadModal;
